/**
 * CrmDealPhaseFilter - Filtro de Fase de Negócio (depende de pipeline)
 * 
 * Permite selecionar uma fase de um pipeline específico.
 * Quando nenhum pipeline está selecionado, o filtro fica desabilitado.
 * 
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-04-23 v1.0.0 - Implementação inicial para listagem de Negócios
 */

import React, { useState } from 'react';
import { Check, ChevronDown, GitBranch } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Tipo de fase retornado pela query
 */
interface PipelinePhase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
}

/**
 * Props do componente CrmDealPhaseFilter
 * @param pipelineId - ID do pipeline (null/'all' = desabilitado)
 * @param value - ID da fase selecionada (null = todas)
 * @param onChange - Callback de mudança
 * @param className - Classes CSS adicionais
 */
interface CrmDealPhaseFilterProps {
  pipelineId: string | null;
  value: string | null;
  onChange: (phaseId: string | null) => void;
  className?: string;
}

/**
 * Componente de filtro de fase
 */
export function CrmDealPhaseFilter({
  pipelineId,
  value,
  onChange,
  className,
}: CrmDealPhaseFilterProps) {
  const [open, setOpen] = useState(false);
  const { currentTenant } = useAuth();

  // Busca fases do pipeline selecionado
  const { data: phases = [] } = useQuery({
    queryKey: ['crm-pipeline-phases-filter', pipelineId, currentTenant?.id],
    queryFn: async () => {
      if (!pipelineId || !currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, sort_order')
        .eq('pipeline_id', pipelineId)
        .eq('tenant_id', currentTenant.id)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data || []) as PipelinePhase[];
    },
    enabled: !!pipelineId && !!currentTenant?.id,
    staleTime: 5 * 60 * 1000,
  });

  const isDisabled = !pipelineId;
  const selectedPhase = phases.find(p => p.id === value);

  const handleSelect = (phaseId: string | null) => {
    onChange(phaseId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isDisabled}
          title={isDisabled ? 'Selecione um pipeline para filtrar por fase' : undefined}
          className={cn('gap-1.5 font-normal border-0 bg-background rounded-full', className)}
        >
          <GitBranch className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
          {selectedPhase ? (
            <span className="flex items-center gap-1.5 truncate">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: selectedPhase.color || '#94a3b8' }}
              />
              <span className="truncate max-w-[80px]">{selectedPhase.name}</span>
            </span>
          ) : (
            <span className="truncate">Fase</span>
          )}
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar fase..." />
          <CommandList>
            <CommandEmpty>Nenhuma fase encontrada.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    'mr-2 h-4 w-4 rounded-full border flex items-center justify-center',
                    value === null ? 'border-primary bg-primary' : 'border-muted'
                  )}
                >
                  {value === null && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                </div>
                <span>Todas Fases</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Fases">
              {phases.map(phase => (
                <CommandItem
                  key={phase.id}
                  value={phase.name}
                  onSelect={() => handleSelect(phase.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      'mr-2 h-4 w-4 rounded-full border flex items-center justify-center',
                      value === phase.id ? 'border-primary bg-primary' : 'border-muted'
                    )}
                  >
                    {value === phase.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </div>
                  <span
                    className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                    style={{ backgroundColor: phase.color || '#94a3b8' }}
                  />
                  <span className="truncate">{phase.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
